import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ComponentRef
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import {TextFormComponent} from '../form-types/text/text-form.component';
import {NumberFormComponent} from '../form-types/number/number-form.component';
import {DateFormComponent} from '../form-types/date/date-form.component';
import {DaterangeFormComponent} from '../form-types/daterange/daterange-form.component';
import {TimeFormComponent} from '../form-types/time/time-form.component';
import {EnumFormComponent} from '../form-types/enum/enum-form.component';
import {BooleanFormComponent} from '../form-types/boolean/boolean-form.component';
import { DynamicFormBuilder } from '../../services/dynamic-form-builder.service';
import { FormParams } from '../../interfaces/form-params';
import { AutocompleteComponent } from '../form-types/autocomplete/autocomplete.component';

enum InputTypes {
  TEXT = 'string',
  NUMBER = 'number',
  DATE = 'date',
  DATE_RANGE = 'daterange',
  TIME = 'time',
  ENUM = 'enum',
  BOOL = 'bool',
  AUTOCOMPLETE = 'autoComplete',
}

const InputTypeColSizes = {
  [InputTypes.TEXT]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.NUMBER]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.DATE]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.DATE_RANGE]: 'col-md-8 col-lg-6 col-xl-4',
  [InputTypes.TIME]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.ENUM]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.BOOL]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.BOOL]: 'col-md-4 col-lg-3 col-xl-2',
};

interface SearchByParams {
  [key: string]: InputTypes;
}


@Component({
  selector: 'bm-form-template',
  templateUrl: './form-template.component.html',
  styleUrls: ['./form-template.component.scss']
})

export class FormTemplateComponent implements OnInit, AfterViewInit {

  private _params: SearchByParams;
  @Input() initialValues: any;

  @Input() set params(params) {
    if (JSON.stringify(params) !== JSON.stringify(this._params)) {
      this._params = params;
      this.initFormFields();
      this.fillFormFieldValues();
      this.renderFilters();
    } else {
      this.fillFormFieldValues();
    }
  }

  get params() {
    return this._params;
  }

  @Output() search = new EventEmitter();
  @Input() mutateQueryString = true;

  @ViewChild('vcRef', {static: true, read: ViewContainerRef})
  vcRef: ViewContainerRef;

  forms: FormParams[] = [];

  readonly formFieldComponents = {
    [InputTypes.TEXT]: TextFormComponent,
    [InputTypes.NUMBER]: NumberFormComponent,
    [InputTypes.DATE]: DateFormComponent,
    [InputTypes.TIME]: TimeFormComponent,
    [InputTypes.ENUM]: EnumFormComponent,
    [InputTypes.DATE_RANGE]: DaterangeFormComponent,
    [InputTypes.BOOL]: BooleanFormComponent,
    [InputTypes.AUTOCOMPLETE]: AutocompleteComponent,
  };

  constructor(
    private factoryResolver: ComponentFactoryResolver,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private dfb: DynamicFormBuilder,
  ) {
  }

  public static getFormLabel(name: string) {
    const label = name.replace(/_/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  ngOnInit() {
    this.initFormFields();
    this.fillFormFieldValues();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.renderFilters();
    });
  }

  initFormFields() {
    if (!this.params) {
      return;
    }

    this.forms = Object.entries(this.params).map(entry => this.dfb.createForm(entry));

  }

  fillFormFieldValues() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const formFieldsWithValues = [];

    Object.entries(this.initialValues as {[key: string]: string} || queryParams).forEach(entry => {
      this.forms.forEach(filter => {
        const touched = this.dfb.setFormValue(entry, filter);
        if (touched) {
          formFieldsWithValues.push(filter);
        }
      });
    });

    this.forms.forEach(formField => {
      if (!formFieldsWithValues.includes(formField) && formField.type !== 'daterange') {
        formField.value = null;
      }
    });
  }

  renderFilters() {
    this.vcRef.clear();
    this.forms.forEach(filter => {
      this.renderFilterControl(filter);
    });
  }

  renderFilterControl(filterControl: FormParams) {
    const factory = this.factoryResolver.resolveComponentFactory(filterControl.component);
    const componentRef: ComponentRef<any> = this.vcRef.createComponent(factory);

    componentRef.instance.className = InputTypeColSizes[filterControl.type];
    componentRef.instance.params = filterControl;
  }

  performSearch() {
    const searchParams = {};
    this.forms.forEach(formField => {
      if (formField.value === null) {
        return;
      }
      if (formField.type === InputTypes.DATE_RANGE) {
        searchParams[`start_${formField.name}`] = formField.value.start;
        searchParams[`end_${formField.name}`] = formField.value.end;
      } else {
        searchParams[formField.name] = formField.value;
      }
    });

    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] === null || !Array.isArray(searchParams[key])) {
        return;

      } else {
        searchParams[key] = searchParams[key].map(params => {
          const targetKey = Object.keys(params).find(k => k.startsWith('_'));

          return {
            name: params.name,
            [targetKey.slice(1)]: params[targetKey],
          };

        });
      }
    });

    this.search.emit(searchParams);

    const queryParams = this.dfb.mapSearchByFields(searchParams, {});

    if (this.mutateQueryString) {
      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams,
          queryParamsHandling: 'merge'
        }
      );
    }
  }

  clearSearchForm() {
    this.forms.forEach(item => {
      switch (item.type) {
        case InputTypes.BOOL:
          item.value = null;
          item.compactValue = 'all';
          break;
        default:
          item.value = null;
          item.compactValue = null;
      }
    });
  }

}
